import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PaginationRequest,
  PerformanceFilter,
  Product,
  ProductDetails,
  ProductDetailsSections,
  ProductPerformanceList,
  Wallet,
  WalletSummary,
} from '../../../../model';
import { ProductUnderlyingGroupsPaginated } from '../../../../model/wallet/details/product-underlying-groups-paginated';
import {
  PortfolioSummaryByMacroCategory,
  RealEconomyAssetSummary,
  RealEconomyProductsConcentration,
  ResidueDuration,
  RiskProfile,
} from '../../../../model/wallet/wallet-ow';
import {
  addAllWalletBasicExtraReducers,
  addWalletPendingAndErrorExtraReducers,
} from '../../../../utils/action-builder-utils';
import { defaultPagination, getProductMacroCategory } from '../../../../utils';
import {
  downloadWalletFile,
  retrievePortfolioCompositionMacroCategory,
  retrieveProductsPerformances,
  retrieveProductTransactions,
  retrieveProductUnderlying,
  retrieveRealEconomyAssetSummary,
  retrieveRealEconomyProductsConcentration,
  retrieveRealEconomyResidueDuration,
  retrieveRealEconomyRiskProfile,
  retrieveWallet,
} from './my-wallet-thunk';
import {
  MyWalletSections,
  MyWalletState,
  WalletPayloadAction,
} from './my-wallet-types';

const checkMyWalletSection = <T>(
  action: PayloadAction<WalletPayloadAction<T>>
): boolean => action.payload.selectedSection === 'MY_WALLET';

const checkMyWalletOrPirBoxSection = <T>(
  action: PayloadAction<WalletPayloadAction<T>>
): boolean =>
  action.payload.selectedSection === 'MY_WALLET' ||
  action.payload.selectedSection === 'PIR_BOX';

const initialPerformanceFilter: PerformanceFilter = {
  macroCategory: 'Managed',
  dateType: 'START_YEAR',
  showClosedPositions: true
};

const initialProductDetails: ProductDetails = {
  contract: null,
  category: null,
  transactionsPaginated: {
    items: {},
    pageInfo: defaultPagination,
  },
  underlyingGroups: {
    type: null,
    underlyingGroupsPaginated: {
      items: {},
      pageInfo: defaultPagination,
    },
  },
};

const initialState: MyWalletState = {
  wallet: null,
  flagOpenProductDetails: false,
  flagOpenDateFilter: false,
  productDetails: initialProductDetails,
  productDetailsSelectedSection: 'TRANSACTIONS',
  performanceFilter: initialPerformanceFilter,
  selectedProduct: null,
  summary: null,
  walletRequestStatus: 'idle',
  productsPerformancesRequestStatus: 'idle',
  productTransactionsRequestStatus: 'idle',
  productUnderlyingRequestStatus: 'idle',
  fileDownloadedRequestStatus: 'idle',
  flagShowBalance: false,
  portfolioSummary: null,
  portfolioSummaryRequestStatus: 'idle',
  realEconomyAssetSummary: null,
  realEconomyAssetSummaryRequestStatus: 'idle',
  realEconomyResidueDuration: null,
  realEconomyResidueDurationRequestStatus: 'idle',
  realEconomyRiskProfile: null,
  realEconomyRiskProfileRequestStatus: 'idle',
  realEconomyProductsConcentration: null,
  realEconomyProductsConcentrationRequestStatus: 'idle',
  myWalletSection: 'POSITION_LIST',
};

const myWalletSlice = createSlice({
  name: 'features/walletStore/myWallet',
  initialState: () => {
    return initialState;
  },
  reducers: {
    resetWalletState() {
      return initialState;
    },
    clearWalletError(state) {
      state.walletError = undefined;
      state.walletRequestStatus = 'idle';
    },
    clearProductTransactionsError(state) {
      state.productTransactionsError = undefined;
      state.productTransactionsRequestStatus = 'idle';
    },
    clearProductUnderlyingError(state) {
      state.productUnderlyingError = undefined;
      state.productUnderlyingRequestStatus = 'idle';
    },
    clearProductsPerformancesError(state) {
      state.productsPerformancesError = undefined;
      state.productsPerformancesRequestStatus = 'idle';
    },
    setMyWalletSection(state, action: PayloadAction<MyWalletSections>) {
      state.myWalletSection = action.payload;
    },
    setSelectedProduct(state, action: PayloadAction<Product>) {
      const macroCategory = getProductMacroCategory(
        state.wallet,
        action.payload.isin
      );
      if (macroCategory !== 'TraditionalInsurance') {
        state.selectedProduct = action.payload;
        state.flagOpenProductDetails = true;
        state.productDetails = {
          ...initialProductDetails,
          contract: action.payload.contract,
          category: macroCategory,
        };
      }
    },
    clearSelectedProduct(state) {
      state.selectedProduct = null;
      state.flagOpenProductDetails = false;
      state.productDetails = initialProductDetails;
    },
    setDateFilterOpened(state, action: PayloadAction<boolean>) {
      state.flagOpenDateFilter = action.payload;
    },
    setProductDetailsSelectedSection(
      state,
      action: PayloadAction<ProductDetailsSections>
    ) {
      state.productDetailsSelectedSection = action.payload;
    },
    clearProductDetails(state) {
      state.productDetails = initialProductDetails;
      state.productDetailsSelectedSection = 'TRANSACTIONS';
    },
    setPerformanceFilter(state, action: PayloadAction<PerformanceFilter>) {
      state.performanceFilter = action.payload;
    },
    updateFlagShowBalance(state, action: PayloadAction<boolean>) {
      state.flagShowBalance = action.payload;
    },
    setPageTransactions(state, action: PayloadAction<PaginationRequest>) {
      state.productDetails.transactionsPaginated.pageInfo = {
        ...state.productDetails.transactionsPaginated.pageInfo,
        ...action.payload,
      };
    },
    setPageUnderlying(state, action: PayloadAction<PaginationRequest>) {
      state.productDetails.underlyingGroups.underlyingGroupsPaginated.pageInfo =
        {
          ...state.productDetails.underlyingGroups.underlyingGroupsPaginated
            .pageInfo,
          ...action.payload,
        };
    },
    clearFileDownloadedError(state) {
      state.fileDownloadedError = undefined;
      state.fileDownloadedRequestStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    addWalletPendingAndErrorExtraReducers(
      builder,
      retrieveWallet,
      'wallet',
      checkMyWalletSection
    );
    builder.addCase(
      retrieveWallet.fulfilled,
      (
        state,
        action: PayloadAction<
          WalletPayloadAction<{ wallet: Wallet; summary: WalletSummary }>
        >
      ) => {
        if (checkMyWalletSection(action)) {
          state.walletRequestStatus = 'succeeded';
          state.wallet = action.payload.payload.wallet;
          state.summary = action.payload.payload.summary;
        }
      }
    );
    builder.addCase(
      retrieveProductsPerformances.fulfilled,
      (state, action: PayloadAction<WalletPayloadAction<ProductPerformanceList>>) => {
        if (checkMyWalletSection(action)) {
          state.wallet = {
            ...state.wallet,
            performances: action.payload.payload,
          };
          state.productsPerformancesRequestStatus = 'succeeded';
        }
      }
    );
    addWalletPendingAndErrorExtraReducers(
      builder,
      retrieveProductsPerformances,
      'productsPerformances',
      checkMyWalletSection
    );
    builder.addCase(
      retrieveProductTransactions.fulfilled,
      (
        state,
        action: PayloadAction<
          WalletPayloadAction<
            Omit<ProductDetails, 'contract' | 'category' | 'underlyingGroups'>
          >
        >
      ) => {
        if (checkMyWalletSection(action)) {
          state.productDetails = {
            ...state.productDetails,
            ...action.payload.payload,
          };
          state.productTransactionsRequestStatus = 'succeeded';
        }
      }
    );
    addWalletPendingAndErrorExtraReducers(
      builder,
      retrieveProductTransactions,
      'productTransactions',
      checkMyWalletSection
    );
    builder.addCase(
      retrieveProductUnderlying.fulfilled,
      (
        state,
        action: PayloadAction<
          WalletPayloadAction<ProductUnderlyingGroupsPaginated>
        >
      ) => {
        if (checkMyWalletSection(action)) {
          state.productDetails = {
            ...state.productDetails,
            underlyingGroups: action.payload.payload,
          };
          state.productUnderlyingRequestStatus = 'succeeded';
        }
      }
    );
    builder.addCase(
      retrievePortfolioCompositionMacroCategory.fulfilled,
      (state, action: PayloadAction<PortfolioSummaryByMacroCategory>) => {
        state.portfolioSummary = action.payload;
        state.portfolioSummaryRequestStatus = 'succeeded';
      }
    );
    builder.addCase(
      retrievePortfolioCompositionMacroCategory.pending,
      (state) => {
        state.portfolioSummaryRequestStatus = 'loading';
      }
    );
    builder.addCase(
      retrievePortfolioCompositionMacroCategory.rejected,
      (state) => {
        state.portfolioSummaryRequestStatus = 'failed';
      }
    );
    builder.addCase(
      retrieveRealEconomyAssetSummary.fulfilled,
      (state, action: PayloadAction<RealEconomyAssetSummary>) => {
        state.realEconomyAssetSummary = action.payload;
        state.realEconomyAssetSummaryRequestStatus = 'succeeded';
      }
    );
    builder.addCase(retrieveRealEconomyAssetSummary.pending, (state) => {
      state.realEconomyAssetSummaryRequestStatus = 'loading';
    });
    builder.addCase(retrieveRealEconomyAssetSummary.rejected, (state) => {
      state.realEconomyAssetSummaryRequestStatus = 'failed';
    });
    builder.addCase(
      retrieveRealEconomyProductsConcentration.fulfilled,
      (state, action: PayloadAction<RealEconomyProductsConcentration>) => {
        state.realEconomyProductsConcentration = action.payload;
        state.realEconomyProductsConcentrationRequestStatus = 'succeeded';
      }
    );
    builder.addCase(
      retrieveRealEconomyProductsConcentration.pending,
      (state) => {
        state.realEconomyProductsConcentrationRequestStatus = 'loading';
      }
    );
    builder.addCase(
      retrieveRealEconomyProductsConcentration.rejected,
      (state) => {
        state.realEconomyProductsConcentrationRequestStatus = 'failed';
      }
    );
    builder.addCase(
      retrieveRealEconomyResidueDuration.fulfilled,
      (state, action: PayloadAction<ResidueDuration>) => {
        state.realEconomyResidueDuration = action.payload;
        state.realEconomyResidueDurationRequestStatus = 'succeeded';
      }
    );
    builder.addCase(retrieveRealEconomyResidueDuration.pending, (state) => {
      state.realEconomyResidueDurationRequestStatus = 'loading';
    });
    builder.addCase(retrieveRealEconomyResidueDuration.rejected, (state) => {
      state.realEconomyResidueDurationRequestStatus = 'failed';
    });
    builder.addCase(
      retrieveRealEconomyRiskProfile.fulfilled,
      (state, action: PayloadAction<RiskProfile>) => {
        state.realEconomyRiskProfile = action.payload;
        state.realEconomyRiskProfileRequestStatus = 'succeeded';
      }
    );
    builder.addCase(retrieveRealEconomyRiskProfile.pending, (state) => {
      state.realEconomyRiskProfileRequestStatus = 'loading';
    });
    builder.addCase(retrieveRealEconomyRiskProfile.rejected, (state) => {
      state.realEconomyRiskProfileRequestStatus = 'failed';
    });
    addWalletPendingAndErrorExtraReducers(
      builder,
      retrieveProductUnderlying,
      'productUnderlying',
      checkMyWalletSection
    );
    addAllWalletBasicExtraReducers(
      builder,
      downloadWalletFile,
      'fileDownloaded',
      checkMyWalletOrPirBoxSection
    );
  },
});

export const myWalletSliceActions = myWalletSlice.actions;

export default myWalletSlice.reducer;
